import { useEffect, useMemo, useState, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { CONTRAT_STATUT_LABELS, contratReference } from "@/pages/proprietaire/contrats/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import { contratPeriodKeys, monthLabel, toMonthKey } from "@/pages/proprietaire/loyers/periods";
import { usePaiements } from "../PaiementsProvider";
import { MODE_PAIEMENT_OPTIONS, type ModePaiement, type Paiement, type PaiementInput } from "../types";

interface PaiementFormProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (paiement: Paiement) => void;
  defaultContratId?: string;
  defaultPeriode?: string;
  defaultLocataireId?: string;
  defaultBienId?: string;
  defaultImmeubleId?: string;
}

interface FormState {
  locataireId: string;
  bienId: string;
  contratId: string;
  periode: string;
  montant: string;
  date: string;
  mode: ModePaiement;
  reference: string;
  note: string;
}

const EMPTY_STATE: FormState = {
  locataireId: "",
  bienId: "",
  contratId: "",
  periode: "",
  montant: "",
  date: "",
  mode: "especes",
  reference: "",
  note: "",
};

export default function PaiementForm({
  open,
  onClose,
  onCreated,
  defaultContratId,
  defaultPeriode,
  defaultLocataireId,
  defaultBienId,
  defaultImmeubleId,
}: PaiementFormProps) {
  const { contrats, getContrat } = useContrats();
  const { biens } = useBiens();
  const { immeubles } = useImmeubles();
  const { locataires } = useLocataires();
  const { addPaiement } = usePaiements();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setForm(() => {
      const base: FormState = { ...EMPTY_STATE, date: new Date().toISOString().slice(0, 10) };
      if (defaultContratId) {
        const contrat = getContrat(defaultContratId);
        if (contrat) {
          base.contratId = contrat.id;
          base.locataireId = contrat.locataireId;
          base.bienId = contrat.bienId;
          base.montant = String(contrat.loyerMensuel || "");
          const keys = contratPeriodKeys(contrat.dateDebut, contrat.dateFin);
          base.periode = defaultPeriode || keys[0] || "";
        }
      } else {
        if (defaultLocataireId) base.locataireId = defaultLocataireId;
        if (defaultBienId) {
          base.bienId = defaultBienId;
          const bien = biens.find((item) => item.id === defaultBienId);
          if (bien) base.locataireId = base.locataireId || "";
        }
        if (defaultImmeubleId) {
          const bien = biens.find((item) => item.immeubleId === defaultImmeubleId);
          if (bien && !base.bienId) base.bienId = bien.id;
        }
      }
      return base;
    });
    setErrors({});
  }, [open, defaultContratId, defaultPeriode, defaultLocataireId, defaultBienId, defaultImmeubleId, getContrat, biens]);

  const locataireOptions = useMemo(
    () => locataires.map((locataire) => ({ value: locataire.id, label: locataireNomComplet(locataire) })),
    [locataires]
  );

  const biensDuLocataire = useMemo(() => {
    if (!form.locataireId) return [];
    const bienIds = new Set(
      contrats.filter((contrat) => contrat.locataireId === form.locataireId).map((contrat) => contrat.bienId)
    );
    return biens.filter((bien) => bienIds.has(bien.id));
  }, [contrats, biens, form.locataireId]);

  const bienOptions = useMemo(
    () =>
      biensDuLocataire.map((bien) => ({
        value: bien.id,
        label: `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
      })),
    [biensDuLocataire]
  );

  const contratsFiltres = useMemo(
    () =>
      contrats.filter(
        (contrat) =>
          contrat.locataireId === form.locataireId && (!form.bienId || contrat.bienId === form.bienId)
      ),
    [contrats, form.locataireId, form.bienId]
  );

  const contratOptions = useMemo(
    () =>
      contratsFiltres.map((contrat) => {
        const immeuble = immeubles.find((item) => item.id === contrat.immeubleId);
        return {
          value: contrat.id,
          label: `${contratReference(contrat.id)} · ${CONTRAT_STATUT_LABELS[contrat.statut]}${
            immeuble ? ` · ${immeuble.nom}` : ""
          }`,
        };
      }),
    [contratsFiltres, immeubles]
  );

  const selectedContrat = form.contratId ? getContrat(form.contratId) : undefined;

  const periodeOptions = useMemo(() => {
    if (!selectedContrat) return [];
    return contratPeriodKeys(selectedContrat.dateDebut, selectedContrat.dateFin).map((key) => ({
      value: key,
      label: monthLabel(key),
    }));
  }, [selectedContrat]);

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "locataireId") {
        next.bienId = "";
        next.contratId = "";
        next.periode = "";
      }
      if (key === "bienId") {
        next.contratId = "";
        next.periode = "";
      }
      if (key === "contratId") {
        const contrat = getContrat(value);
        if (contrat) {
          next.locataireId = contrat.locataireId;
          next.bienId = contrat.bienId;
          if (!prev.montant) next.montant = String(contrat.loyerMensuel || "");
          const keys = contratPeriodKeys(contrat.dateDebut, contrat.dateFin);
          const courant = toMonthKey(new Date());
          next.periode = keys.includes(courant) ? courant : keys[0] || "";
        }
      }
      if (key === "periode") {
        const contrat = getContrat(next.contratId);
        if (contrat) next.montant = String(contrat.loyerMensuel || "");
      }
      return next;
    });
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.locataireId) nextErrors.locataireId = "Sélectionnez le locataire.";
    if (!form.contratId) nextErrors.contratId = "Sélectionnez le contrat.";
    if (!form.periode) nextErrors.periode = "Sélectionnez la période concernée.";
    if (!form.montant.trim() || Number(form.montant) <= 0) nextErrors.montant = "Indiquez un montant valide.";
    if (!form.date) nextErrors.date = "Indiquez la date du paiement.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: PaiementInput = {
      contratId: form.contratId,
      periode: form.periode,
      montant: Math.max(0, Number(form.montant) || 0),
      date: form.date,
      mode: form.mode,
      reference: form.reference.trim(),
      note: form.note.trim(),
    };

    const paiement = addPaiement(input);
    setForm(EMPTY_STATE);
    setErrors({});
    onClose();
    if (onCreated) onCreated(paiement);
  };

  const canSubmit = contrats.length > 0;

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title="Enregistrer un paiement"
      description="Rattachez l'encaissement à un contrat et à une période. Le statut du loyer est mis à jour automatiquement."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="paiement-form"
            disabled={!canSubmit}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer le paiement
          </button>
        </>
      }
    >
      <form id="paiement-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field
            label="Locataire"
            htmlFor="pay-locataire"
            required
            error={errors.locataireId}
            hint="Choisissez un locataire ayant déjà un contrat."
          >
            <Select
              id="pay-locataire"
              value={form.locataireId}
              onChange={(event) => update("locataireId", event.target.value)}
              options={locataireOptions}
              placeholder={locataires.length > 0 ? "Sélectionnez un locataire" : "Créez d'abord un locataire"}
              disabled={locataires.length === 0}
              invalid={Boolean(errors.locataireId)}
            />
          </Field>

          <Field label="Bien" htmlFor="pay-bien" hint="Biens liés aux contrats du locataire.">
            <Select
              id="pay-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={form.locataireId ? "Tous les biens du locataire" : "Choisissez d'abord un locataire"}
              disabled={!form.locataireId || bienOptions.length === 0}
            />
          </Field>

          <Field
            label="Contrat"
            htmlFor="pay-contrat"
            required
            error={errors.contratId}
            className="md:col-span-2"
          >
            <Select
              id="pay-contrat"
              value={form.contratId}
              onChange={(event) => update("contratId", event.target.value)}
              options={contratOptions}
              placeholder={
                !form.locataireId
                  ? "Choisissez d'abord un locataire"
                  : contratsFiltres.length > 0
                    ? "Sélectionnez le contrat"
                    : "Aucun contrat pour ce locataire"
              }
              disabled={contratOptions.length === 0}
              invalid={Boolean(errors.contratId)}
            />
          </Field>

          <Field label="Période" htmlFor="pay-periode" required error={errors.periode}>
            <Select
              id="pay-periode"
              value={form.periode}
              onChange={(event) => update("periode", event.target.value)}
              options={periodeOptions}
              placeholder={selectedContrat ? "Sélectionnez la période" : "Choisissez d'abord un contrat"}
              disabled={periodeOptions.length === 0}
              invalid={Boolean(errors.periode)}
            />
          </Field>

          <Field label="Montant (FCFA)" htmlFor="pay-montant" required error={errors.montant}>
            <Input
              id="pay-montant"
              type="number"
              min={0}
              max={100000000}
              value={form.montant}
              onChange={(event) => update("montant", event.target.value)}
              placeholder="Ex. 75000"
              invalid={Boolean(errors.montant)}
            />
          </Field>

          <Field label="Date du paiement" htmlFor="pay-date" required error={errors.date}>
            <Input
              id="pay-date"
              type="date"
              value={form.date}
              onChange={(event) => update("date", event.target.value)}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field label="Mode de paiement" htmlFor="pay-mode">
            <Select
              id="pay-mode"
              value={form.mode}
              onChange={(event) => update("mode", event.target.value)}
              options={MODE_PAIEMENT_OPTIONS}
            />
          </Field>

          <Field
            label="Référence"
            htmlFor="pay-reference"
            hint="Numéro de transaction, chèque, bordereau..."
            className="md:col-span-2"
          >
            <Input
              id="pay-reference"
              value={form.reference}
              onChange={(event) => update("reference", event.target.value)}
              placeholder="Ex. OM-2026-0918"
            />
          </Field>

          <Field label="Note" htmlFor="pay-note" className="md:col-span-2">
            <Textarea
              id="pay-note"
              value={form.note}
              onChange={(event) => update("note", event.target.value)}
              placeholder="Précisions sur l'encaissement..."
              className="min-h-[80px]"
              maxLength={500}
            />
          </Field>
        </div>

        {!canSubmit && (
          <p className="flex items-center gap-2 rounded-md bg-background-100 px-3.5 py-3 text-xs text-foreground-600">
            <i className="ri-information-line text-base text-foreground-400" aria-hidden="true" />
            Un paiement nécessite un contrat. Créez d'abord un contrat dans le module Contrats.
          </p>
        )}

      </form>
    </Modal>
  );
}